// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/components/listas/PeriodoFiltro.tsx
//  Seletor de período das listas (Hoje, 7/30 dias, mês, tudo, personalizado).
//  O filtro vai ao SERVIDOR como dtInicio/dtFim em yyyy-MM-dd (dia local).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { endOfMonth, format, startOfMonth, subDays } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export type Periodo = 'hoje' | '7dias' | '30dias' | 'mes' | 'todos' | 'personalizado';

export const PERIODOS: { value: Periodo; label: string }[] = [
  { value: 'hoje',          label: 'Hoje' },
  { value: '7dias',         label: 'Últimos 7 dias' },
  { value: '30dias',        label: 'Últimos 30 dias' },
  { value: 'mes',           label: 'Este mês' },
  { value: 'todos',         label: 'Todo o período' },
  { value: 'personalizado', label: 'Personalizado' },
];

/** Dia LOCAL em yyyy-MM-dd (toISOString daria o dia em UTC, que à noite já é amanhã). */
export const diaLocal = (d: Date) => format(d, 'yyyy-MM-dd');

export interface Intervalo { dtInicio?: string; dtFim?: string }

export interface OpcoesPeriodo {
  /**
   * "Este mês" vai até o ÚLTIMO dia do mês, e não só até hoje. Para datas que
   * podem estar no futuro (vencimento): sem isso, o resto do mês sumiria da lista.
   */
  mesInteiro?: boolean;
}

export const intervaloDoPeriodo = (periodo: Periodo, de: string, ate: string, opcoes: OpcoesPeriodo = {}): Intervalo => {
  const hoje = new Date();
  switch (periodo) {
    case 'hoje':   return { dtInicio: diaLocal(hoje), dtFim: diaLocal(hoje) };
    case '7dias':  return { dtInicio: diaLocal(subDays(hoje, 6)), dtFim: diaLocal(hoje) };
    case '30dias': return { dtInicio: diaLocal(subDays(hoje, 29)), dtFim: diaLocal(hoje) };
    case 'mes':    return { dtInicio: diaLocal(startOfMonth(hoje)), dtFim: diaLocal(opcoes.mesInteiro ? endOfMonth(hoje) : hoje) };
    case 'personalizado': return { dtInicio: de || undefined, dtFim: ate || undefined };
    default:       return {};
  }
};

/**
 * Estado do filtro de período. `intervalo` é o que vai para a API; `onChange` é chamado a cada
 * mudança (use para voltar à página 1).
 */
export function usePeriodo(inicial: Periodo, onChange?: () => void, opcoes?: OpcoesPeriodo) {
  const [periodo, setPeriodoState] = useState<Periodo>(inicial);
  const [de, setDeState]   = useState(() => diaLocal(new Date()));
  const [ate, setAteState] = useState(() => diaLocal(new Date()));
  const avisar = <T,>(set: (v: T) => void) => (v: T) => { set(v); onChange?.(); };
  return {
    periodo, de, ate,
    setPeriodo: avisar(setPeriodoState),
    setDe: avisar(setDeState),
    setAte: avisar(setAteState),
    intervalo: intervaloDoPeriodo(periodo, de, ate, opcoes),
  };
}

interface PeriodoFiltroProps {
  estado: ReturnType<typeof usePeriodo>;
  /** Nome da data filtrada, para acessibilidade ("Emissão", "Vencimento"...). */
  rotuloData?: string;
}

export function PeriodoFiltro({ estado, rotuloData = 'Data' }: PeriodoFiltroProps) {
  return (
    <>
      <Select value={estado.periodo} onValueChange={(v) => estado.setPeriodo(v as Periodo)}>
        <SelectTrigger className="w-full sm:w-44" aria-label={`Período (${rotuloData.toLowerCase()})`}>
          <CalendarDays className="h-4 w-4 mr-2" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PERIODOS.map((p) => (
            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {estado.periodo === 'personalizado' && (
        <div className="flex items-center gap-1">
          <Input type="date" aria-label={`${rotuloData} de`} className="w-full sm:w-36" value={estado.de}
            onChange={(e) => estado.setDe(e.target.value)} />
          <span className="text-muted-foreground text-sm">a</span>
          <Input type="date" aria-label={`${rotuloData} até`} className="w-full sm:w-36" value={estado.ate}
            onChange={(e) => estado.setAte(e.target.value)} />
        </div>
      )}
    </>
  );
}
